import type {
  FormSchemaGetListFilterType,
  FormSchemaType,
} from "../schemas/FormSchemaSchemas.ts";
import type {
  FilterObjectType,
  GetListRequestType,
  GetListResponseType,
  GetOneRequestType,
  GetOneResponseType,
  ResourceAdapterType,
} from "../util/index.ts";
import { getFilterObject } from "../util/index.ts";
import { restAPI } from "./index.ts";

export interface FormAdapterType
  extends Pick<
    ResourceAdapterType<FormSchemaType>,
    "getList" | "createOne" | "updateOne"
  > {
  getList: (
    params: GetListRequestType<FilterObjectType<FormSchemaGetListFilterType>>
  ) => Promise<GetListResponseType<FormSchemaType>>;
  getOne: (
    params: GetOneRequestType<{ version?: number }>
  ) => Promise<GetOneResponseType<FormSchemaType>>;
}

export const FormSchemaAdapter: FormAdapterType = {
  /**
   * Get a single form schema (form template)
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/form_schemas/get_form_schema
   */
  getOne: async ({ id, meta }) =>
    restAPI.nodeAPI
      .GET<FormSchemaType>({
        endpoint: `/form_schemas/${id}`,
        ...(meta?.version && { query: { version: meta.version } }),
      })
      .then((resp) => ({ data: resp })),
  /**
   * Search form schemas
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/form_schemas/search_form_schemas
   */
  getList: ({ filters, page, pageSize, sortBy, sortDirection }) =>
    restAPI.nodeAPI.POST({
      endpoint: "/form_schemas/search",
      body: {
        filter: getFilterObject(filters),
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_direction: sortDirection,
      },
    }),
  /**
   * Create a form schema
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/form_schemas/create_form_schema
   */
  createOne: ({ data }) =>
    restAPI.nodeAPI
      .POST<FormSchemaType>({
        endpoint: "/form_schemas",
        body: data,
      })
      .then((resp) => ({ data: resp })),
  /**
   * Update a form schema
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/form_schemas/update_form_schema
   */
  updateOne: ({ id, data }) =>
    restAPI.nodeAPI
      .PUT<FormSchemaType>({
        endpoint: `/form_schemas/${id}`,
        body: data,
      })
      .then((resp) => ({ data: resp })),
};
